import { useCallback, useMemo } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { fetchWidgetData } from '@api/dataSource';
import { convertToWidgetData } from '@common/utils/widgetDataTransform';
import { fetchStatusColorMap } from '@api/statuses';
import { applySeverityColors } from '@common/utils/severityColors';
import { loadMetricValuesData } from '../utils/dashboard/loadMetricValuesData';
import { combineSeriesData, getTableYAxisIndex } from '../utils/dashboard/combineSeriesData';

const EMPTY_WIDGETS = [];

/**
 * Fetches data for a single widget based on its type and config.
 *
 * For line charts with both metric_values and table sources:
 * - Fetches metric_values data for left Y-axis (yAxisIndex: 0)
 * - Fetches table sources in parallel for right Y-axis (yAxisIndex: 1)
 * - Combines all series using combineSeriesData with dualYAxis: true
 */
const fetchSingleWidgetData = async (widget, dashboardFilters) => {
  // Table widgets handle their own pagination - no data needed here
  if (widget.type === 'table' || widget.type === 'table-2d') {
    return { ...widget, data: null };
  }

  const widgetDataSources = widget.config?.dataSources || [];
  const metricValuesSource = widgetDataSources.find((ds) => ds.type === 'metric_values');

  // Line charts with metric_values data source
  if (widget.type === 'line' && metricValuesSource) {
    const tableSources = widgetDataSources.filter((ds) => ds.type === 'table');

    // Single data source (metric_values only) — simple path
    if (tableSources.length === 0) {
      try {
        const data = await loadMetricValuesData(widget, dashboardFilters);
        return { ...widget, data };
      } catch (err) {
        console.error('Metric values fetch failed:', err);
        return { ...widget, data: null };
      }
    }

    // Multiple data sources: fetch all and combine (mirrors preview logic)
    try {
      const allSeriesData = [];

      // 1. Fetch metric_values timeseries data (left Y-axis)
      const metricValuesData = await loadMetricValuesData(widget, dashboardFilters);
      if (metricValuesData?.series) {
        allSeriesData.push(
          ...metricValuesData.series.map((s, idx) => ({
            ...s,
            yAxisIndex: 0,
            categories: idx === 0 ? metricValuesData.categories : undefined,
            isMetricValues: true,
          })),
        );
      }

      // 2. Fetch table data sources in parallel (right Y-axis)
      const tableDataPromises = tableSources.map(async (tableSource, sourceIndex) => {
        try {
          const tableConfig = {
            ...widget.config,
            table: tableSource.id,
          };
          const response = await fetchWidgetData('line', tableConfig, dashboardFilters, false);
          if (response.status === 'success' && response.data?.rows?.length > 0) {
            const tableData = convertToWidgetData(response.data, 'line', tableConfig);
            if (tableData) {
              const yAxisIndex = getTableYAxisIndex(true, sourceIndex);
              if (tableData.series) {
                return tableData.series.map((s, idx) => ({
                  ...s,
                  name: `${tableSource.id}: ${s.name}`,
                  categories: idx === 0 ? tableData.categories : undefined,
                  yAxisIndex,
                }));
              }
              if (tableData.categories && tableData.values) {
                return [{
                  name: tableSource.id,
                  data: tableData.values,
                  categories: tableData.categories,
                  yAxisIndex,
                }];
              }
            }
          }
          return [];
        } catch (err) {
          console.error('Table source fetch failed:', err);
          return [];
        }
      });

      const tableResults = await Promise.all(tableDataPromises);
      tableResults.forEach((series) => allSeriesData.push(...series));

      // 3. Combine all series with dual Y-axis config
      if (allSeriesData.length > 0) {
        const combinedData = combineSeriesData(allSeriesData, {
          metricValuesSource,
          tableSources,
        });
        return { ...widget, data: combinedData };
      }

      return { ...widget, data: null };
    } catch (err) {
      console.error('Combined data sources fetch failed:', err);
      return { ...widget, data: null };
    }
  }

  // Check for valid data sources (query may be in dataSources[].config.query or legacy config.query)
  const hasCustomQuery = widgetDataSources.some((ds) => ds.type === 'custom_query');
  const tableSources = widgetDataSources.filter((ds) => ds.type === 'table');
  const customQuerySource = widgetDataSources.find((ds) => ds.type === 'custom_query');
  const hasQuery = customQuerySource?.config?.query?.trim() || widget.config?.query?.trim();
  const hasValidDataSource =
    (hasCustomQuery && hasQuery) || tableSources.length > 0;

  if (!hasValidDataSource) {
    return { ...widget, data: null };
  }

  // All widget types with table or custom_query (including line + custom query) fetch via widget-data API
  try {
    const response = await fetchWidgetData(
      widget.type,
      widget.config,
      dashboardFilters,
      false // Not in edit mode
    );
    if (response.status === 'success' && response.data?.rows?.length > 0) {
      const transformedData = convertToWidgetData(response.data, widget.type, widget.config);
      return { ...widget, data: transformedData };
    }
    return { ...widget, data: null };
  } catch (err) {
    console.error('Widget data fetch failed:', err);
    return { ...widget, data: null };
  }
};

/**
 * Hook to load widget data for a dashboard
 * Uses one React Query per widget so the grid can render immediately and each
 * widget shows its own loading state while its request completes (no single
 * Promise.all blocking the whole dashboard).
 */
export function useWidgetData(dashboard, filters, options = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const dashboardId = dashboard?.id;
  const widgets = dashboard?.widgets ?? EMPTY_WIDGETS;

  const queryEnabled = enabled && !!dashboardId && widgets.length > 0;

  const { data: statusColorMap } = useQuery({
    queryKey: ['status-color-map'],
    queryFn: fetchStatusColorMap,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const queries = useQueries({
    queries: widgets.map((widget) => ({
      queryKey: [
        'widget-data',
        dashboardId,
        widget.id,
        filters,
        widget.type,
        JSON.stringify(widget.config ?? {}),
      ],
      queryFn: () => fetchSingleWidgetData(widget, filters),
      enabled: queryEnabled,
      gcTime: 0,
      staleTime: 0,
    })),
  });

  const refetch = useCallback(() => {
    if (!dashboardId) {
      return Promise.resolve();
    }
    return queryClient.invalidateQueries({
      queryKey: ['widget-data', dashboardId],
    });
  }, [queryClient, dashboardId]);

  const widgetsWithData = useMemo(() => {
    if (widgets.length === 0) {
      return [];
    }

    return widgets.map((widget, index) => {
      if (!queryEnabled) {
        return { ...widget, data: widget.data ?? null };
      }

      const q = queries[index];
      if (!q) {
        return { ...widget, data: undefined };
      }

      // First fetch in progress — charts treat data === undefined as loading
      if (q.isLoading) {
        return { ...widget, data: undefined };
      }

      if (q.isError) {
        return { ...widget, data: null };
      }

      const enrichedWidget = q.data ?? { ...widget, data: null };

      if (enrichedWidget.data && statusColorMap) {
        return {
          ...enrichedWidget,
          data: applySeverityColors(
            enrichedWidget.data,
            enrichedWidget.type,
            enrichedWidget.config,
            statusColorMap,
          ),
        };
      }

      return enrichedWidget;
    });
  }, [widgets, queries, queryEnabled, statusColorMap]);

  const anyWidgetLoading = queryEnabled && queries.some((q) => q.isLoading);
  const refreshing =
    queryEnabled && queries.some((q) => q.isFetching && !q.isLoading);

  const error = queries.find((q) => q.error)?.error ?? null;

  if (widgets.length === 0) {
    return {
      widgets: [],
      loading: false,
      refreshing: false,
      error: null,
      refetch,
    };
  }

  return {
    widgets: widgetsWithData,
    loading: anyWidgetLoading,
    refreshing,
    error,
    refetch,
  };
}

export default useWidgetData;
